import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import s from './BeforeAfterSection.module.css';
import beforeImage from './images/iris 1 (1).svg';
import afterImage from './images/Vector.png';
import {
    OptimizedMotionH2,
    OptimizedMotionP,
} from '../../../shared/ui/optimized-motion/OptimizedMotion';
import IconSpark from '../../../shared/ui/icons/IconSpark';
import { motion } from 'framer-motion';

const BeforeAfterSection: React.FC = () => {
    const navigate = useNavigate();
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        setSliderPosition(Math.min(Math.max(percentage, 0), 100));
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isDragging || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        setSliderPosition(Math.min(Math.max(percentage, 0), 100));
    };

    useEffect(() => {
        const preventSelection = (e: Event) => e.preventDefault();

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleMouseUp);
            document.addEventListener('selectstart', preventSelection);
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleMouseUp);
            document.removeEventListener('selectstart', preventSelection);
            document.body.style.userSelect = '';
        };
    }, [isDragging]);

    const handleTransformPhoto = () => {
        navigate('/contact');
    };

    return (
        <section className={s.container}>
            <div className={s.content}>
                <OptimizedMotionH2 type="slide-up" delay={0.3} className={s.mainTitle}>
                    <span className={s.highlight}>Transform your brand visuals</span>
                    <br />
                    fast and flawlessly.
                </OptimizedMotionH2>

                <div className={s.comparisonWrapper}>
                    <div className={s.leftContent}>
                        <OptimizedMotionH2 type="slide-up" delay={0.8} className={s.sectionTitle}>
                            Turn your photos into visuals that sell
                        </OptimizedMotionH2>
                        <OptimizedMotionP type="slide-up" delay={1.0} className={s.description}>
                            Your product already looks great — it just needs to be seen that way.
                            SimpleeCreate transforms simple shots into scroll-stopping, on-brand
                            visuals in seconds. <br />→ Because design shouldn't be what's holding
                            your brand back.
                        </OptimizedMotionP>
                        <OptimizedMotionP
                            type="slide-up"
                            delay={1.1}
                            className={s.transformButton}
                            onClick={handleTransformPhoto}
                        >
                            Transform my photo
                        </OptimizedMotionP>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.0 }}
                        className={s.comparisonContainer}
                        ref={containerRef}
                    >
                        <div className={s.imageWrapper}>
                            <div className={s.beforeImage}>
                                <img src={beforeImage} alt="Before" className={s.image} />
                            </div>

                            <div
                                className={s.afterImage}
                                style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                            >
                                <img src={afterImage} alt="After" className={s.image} />
                            </div>

                            <span className={s.label + ' ' + s.labelBefore}>Before</span>
                            <span className={s.label + ' ' + s.labelAfter}>After</span>

                            <div
                                className={s.slider}
                                style={{ left: `${sliderPosition}%` }}
                                onMouseDown={handleMouseDown}
                                onTouchStart={handleTouchStart}
                            >
                                <div className={s.sliderLine} />
                                <div className={s.sliderHandle}>
                                    <IconSpark />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default BeforeAfterSection;
